import React, { useMemo } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EvalState, MatchRow, ParsedQuery } from './types';
import {
  Jurisdiction,
  ScreeningProfile,
  partnerPhrase,
  routeLabel,
} from './profiles';
import { EVAL_LIMIT, querySubject, riskTone, titleCase } from './utils';
import ViewMore from './ViewMore';
import VerdictBox, {
  BoxJump,
  BoxSkeleton,
  BoxTrack,
  VerdictHeadProps,
} from './VerdictBox';

interface Props {
  profile: ScreeningProfile;
  evalState: EvalState;
  parsed: ParsedQuery;
  jurisdiction: Jurisdiction;
  partner: string;
  rows: MatchRow[];
  total: number;
  partial: boolean;
  showEval: boolean;
  listFilter: string;
  onPickList: (list: string) => void;
  onOpenEval: () => void;
  onAddPartner: () => void;
  onFocusQuery: () => void;
  onOpenFilters: () => void;
  onRetry: () => void;
  onViewResults: () => void;
  onNextStep?: () => void;
}

const evalHead = (
  evalState: EvalState,
  rating: string,
  sub?: string,
): Pick<VerdictHeadProps, 'glyphTone' | 'state' | 'word' | 'tone' | 'sub'> => {
  if (evalState.status === 'wait') {
    return { state: 'wait' };
  }
  if (evalState.status === 'done') {
    const tone = riskTone(rating);
    return {
      state: 'value',
      glyphTone: tone,
      word: rating ? titleCase(rating) : 'No rating',
      tone: tone,
      sub: sub,
    };
  }
  return {
    state: 'flat',
    tone: 'none',
    word: evalState.status === 'error' ? 'Unavailable' : 'Not evaluated',
  };
};

const ResultBoxes: React.FC<Props> = (props) => {
  const { profile, evalState: ev, parsed, rows, total } = props;
  const data = ev.data;
  const partnerKey = profile.partner.key;
  const route = routeLabel(profile, props.jurisdiction, props.partner);

  const lists = useMemo(() => {
    const counts: Record<string, number> = {};
    const agency: Record<string, string> = {};
    rows.forEach((r) => {
      counts[r.list] = (counts[r.list] || 0) + 1;
      agency[r.list] = r.agency;
    });
    const keys = Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a] || a.localeCompare(b),
    );
    const max = keys.length ? counts[keys[0]] : 1;
    return {
      keys: keys,
      counts: counts,
      agency: agency,
      max: max,
      agencies: new Set(rows.map((r) => r.agency)).size,
    };
  }, [rows]);

  const missingLine =
    ev.missing === 'both'
      ? `an HTS code and ${partnerKey}`
      : ev.missing === 'hts'
      ? 'an HTS code'
      : `a country of ${partnerKey}`;
  const matchesPhrase = `${total.toLocaleString()} ${
    total === 1 ? 'match' : 'matches'
  }`;
  const noneProse =
    ev.missing === 'both'
      ? `The evaluation reads an HTS code and a country of ${partnerKey}, and this screen has neither. The ${matchesPhrase} below cover every country.`
      : ev.missing === 'hts'
      ? `The evaluation reads an HTS code, so a description-only screen is not evaluated. The ${matchesPhrase} below are complete.`
      : `Add a country of ${partnerKey} to evaluate risk and burden and narrow the matches to that country. The ${matchesPhrase} below cover every country.`;
  const narrowBy =
    parsed.kind === 'hts' && parsed.digits < 10
      ? `Add filters, or use a longer HTS code than ${parsed.fmt}, and run again.`
      : 'Add filters and run again.';
  const skippedProse = `The evaluation reads up to ${EVAL_LIMIT.toLocaleString()} matches and this screen found ${total.toLocaleString()}. ${narrowBy}`;
  const keyFactors = data?.key_factors?.length || 0;
  const evaluatedOn = `Evaluated ${data?.screenDate || 'today'}`;

  const riskFoot =
    ev.status === 'done'
      ? evaluatedOn
      : ev.status === 'wait'
      ? `Reading ${lists.keys.length} ${
          lists.keys.length === 1 ? 'list' : 'lists'
        }`
      : ev.status === 'none'
      ? `Needs ${missingLine}`
      : ev.status === 'skipped'
      ? `Over ${EVAL_LIMIT.toLocaleString()} matches`
      : 'Evaluation failed';

  const riskBox = (
    <VerdictBox
      head={{
        glyph: GppMaybeOutlinedIcon,
        label: `${profile.direction} risk`,
        note: 'AI evaluation',
        ...evalHead(
          ev,
          data?.risk_level || '',
          `${keyFactors} key ${keyFactors === 1 ? 'factor' : 'factors'}`,
        ),
      }}
      foot={riskFoot}
      actions={
        <>
          {ev.status === 'done' && (
            <BoxJump onClick={props.onOpenEval} glyph={ChevronRightIcon}>
              Full evaluation
            </BoxJump>
          )}
          {ev.status === 'none' && ev.missing === 'partner' && (
            <BoxJump onClick={props.onAddPartner}>Add {partnerKey}</BoxJump>
          )}
          {ev.status === 'none' && ev.missing !== 'partner' && (
            <BoxJump onClick={props.onFocusQuery}>Add HTS code</BoxJump>
          )}
          {ev.status === 'skipped' && (
            <BoxJump onClick={props.onOpenFilters}>Filters</BoxJump>
          )}
          {ev.status === 'error' && (
            <BoxJump onClick={props.onRetry} glyph={RefreshIcon} glyphFirst>
              Retry evaluation
            </BoxJump>
          )}
        </>
      }
    >
      {ev.status === 'wait' && (
        <>
          <BoxTrack />
          <BoxSkeleton />
        </>
      )}
      {ev.status === 'done' && data && (
        <ViewMore
          text={data.explanation || 'The evaluation returned no explanation.'}
        />
      )}
      {ev.status === 'none' && <ViewMore text={noneProse} />}
      {ev.status === 'skipped' && <ViewMore text={skippedProse} />}
      {ev.status === 'error' && (
        <div className='psc-alert'>
          The evaluation service did not respond. Your matches are unaffected.
          Retry runs the evaluation again on these {matchesPhrase}; it does not
          re-run the screen.
        </div>
      )}
    </VerdictBox>
  );

  const showsCategory = props.jurisdiction.showsTradeCategory;
  const category =
    showsCategory && ev.status === 'done' && data?.trade_category
      ? titleCase(data.trade_category)
      : '';
  const facts = [
    ...(showsCategory
      ? [{ k: 'Trade category', v: category || '—', empty: !category }]
      : []),
    { k: 'Route', v: route, empty: !props.partner },
    {
      k: 'HTS evaluated',
      v: parsed.kind === 'hts' ? parsed.fmt : '—',
      empty: parsed.kind !== 'hts',
    },
  ];

  const burdenBox = (
    <VerdictBox
      head={{
        glyph: BalanceOutlinedIcon,
        label: 'Trade burden',
        note: 'AI evaluation',
        ...evalHead(ev, data?.burden || '', category),
      }}
      foot={
        ev.status === 'done'
          ? evaluatedOn
          : ev.status === 'wait'
          ? 'Same evaluation as risk'
          : ''
      }
      actions={
        ev.status === 'done' &&
        props.onNextStep && (
          <BoxJump onClick={props.onNextStep} glyph={NorthEastIcon}>
            {profile.nextStep.label}
          </BoxJump>
        )
      }
    >
      {ev.status === 'wait' && <BoxTrack />}
      {ev.status === 'none' && <ViewMore text={profile.copy.burden} />}
      {ev.status === 'skipped' && (
        <ViewMore
          text={`Waits on the same evaluation as risk. It runs once the screen is under ${EVAL_LIMIT.toLocaleString()} matches.`}
        />
      )}
      {ev.status !== 'none' && ev.status !== 'skipped' && (
        <div className='psc-facts'>
          {facts.map((f) => (
            <div key={f.k} className='psc-fact'>
              <span className='k'>{f.k}</span>
              <span className={`v${f.empty ? ' is-empty' : ''}`}>{f.v}</span>
            </div>
          ))}
        </div>
      )}
    </VerdictBox>
  );

  const shownLists = lists.keys.slice(0, 4);
  const more = lists.keys.length - shownLists.length;
  const listsBox = (
    <VerdictBox
      head={{
        glyph: FormatListBulletedIcon,
        glyphTone: lists.keys.length ? 'medium' : 'low',
        label: 'Lists matched',
        note: props.partial ? 'from this page of results' : 'from the results',
        state: 'flat',
        word: `${lists.keys.length} ${
          lists.keys.length === 1 ? 'list' : 'lists'
        }`,
        sub: `${rows.length.toLocaleString()} ${
          rows.length === 1 ? 'product' : 'products'
        }, ${lists.agencies} ${lists.agencies === 1 ? 'agency' : 'agencies'}`,
      }}
      foot={
        lists.keys.length === 0
          ? 'Nothing to review'
          : more > 0
          ? `Top 4 of ${lists.keys.length} lists · the rest are in the table`
          : 'Click a list to find its rows'
      }
      actions={
        lists.keys.length > 0 && (
          <BoxJump onClick={props.onViewResults} glyph={ArrowDownwardIcon}>
            View results
          </BoxJump>
        )
      }
    >
      {lists.keys.length === 0 ? (
        <ViewMore
          text={`No ${
            props.jurisdiction.noun
          } list in scope names ${querySubject(
            parsed,
            'these filters',
          )}${partnerPhrase(
            profile,
            props.partner,
          )}. Save the ticket to keep this clean result on file.`}
        />
      ) : (
        <div className='psc-lists'>
          {shownLists.map((k) => {
            const on = props.listFilter === k;
            return (
              <button
                key={k}
                type='button'
                className='psc-listrow'
                aria-pressed={on}
                title={
                  on ? 'Clear the search' : 'Search the table for this list'
                }
                onClick={() => props.onPickList(on ? '' : k)}
              >
                <span className='psc-grow'>
                  <span className='name'>{k}</span>
                  <span className='sub'>{lists.agency[k]}</span>
                </span>
                <span className='psc-bar'>
                  <span
                    style={{
                      width: `${Math.round(
                        (lists.counts[k] / lists.max) * 100,
                      )}%`,
                    }}
                  />
                </span>
                <span className='n'>{lists.counts[k].toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}
    </VerdictBox>
  );

  return (
    <div
      className={`psc-boxes${props.showEval ? '' : ' psc-boxes--lists-only'}`}
    >
      {props.showEval && riskBox}
      {props.showEval && burdenBox}
      {listsBox}
    </div>
  );
};

export default ResultBoxes;
